import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { usePrevious } from "react-use";

import { TextInputOption, TextInputProps } from "./types";

export interface UseTextInputProps
  extends Pick<TextInputProps, "options" | "transformOptions" | "mode" | "type" | "clearable"> {
  value?: string;
  defaultValue?: string;
}

export interface UseTextInputReturn {
  text: string;
  setText: Dispatch<SetStateAction<string>>;
  isLoading: boolean;
  transformedOptions: TextInputOption[] | undefined;
  showOptions: boolean;
  setShowOptions: Dispatch<SetStateAction<boolean>>;
}

export function useTextInput({
  mode,
  options,
  value,
  defaultValue,
  transformOptions,
}: UseTextInputProps): UseTextInputReturn {
  const [text, setText] = useState(value ?? defaultValue ?? "");
  const previousText = usePrevious(text);

  const [showOptions, setShowOptions] = useState(false);
  const [isLoading, setLoading] = useState(false);

  // by default, options are filtered away if current text is not a substring (not case sensitive)
  const defaultTransformOptions = useCallback(() => {
    return options
      ?.filter(({ label }) => label.toLowerCase().includes(text.toLowerCase()))
      .slice(0, 10);
  }, [options, text]);

  // used to keep a filtered array of options
  const [transformedOptions, setTransformedOptions] = useState<TextInputOption[] | undefined>(
    options
  );
  // options can be filtered, sorted, etc. based on current text
  useEffect(() => {
    (async () => {
      // in select mode we don't want to filter by current text as default filtering behaviour
      const transformFn =
        mode === "select" ? transformOptions : transformOptions ?? defaultTransformOptions;

      if (!transformFn || previousText === text) return;

      let newOptions = transformFn(text);
      if (!Array.isArray(newOptions)) {
        setLoading(true);
      }
      newOptions = await transformFn(text);
      setLoading(false);
      setTransformedOptions(newOptions);
    })();
  }, [transformOptions, mode, text, previousText, showOptions, defaultTransformOptions]);

  // update internal text state when value changes from outside
  useEffect(() => {
    if (value !== undefined) {
      setText(value);
    }
  }, [value]);

  return useMemo<UseTextInputReturn>(
    () => ({
      text,
      setText,
      showOptions,
      setShowOptions,
      isLoading,
      transformedOptions,
    }),
    [isLoading, showOptions, text, setText, transformedOptions]
  );
}
